import React, { useMemo, useState } from "react";
import { Box, Link, Stack, TextField, Typography } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../../common/CommonLoader/CommonLoader";
import AppButton from "../../../common/AppButton";
import {
  useBillingPeriods,
  useCreateBillingPeriod
} from "../../../../hooks/api/education/useAcademicStructure";

const BillingPeriodDetail: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { periodId = "" } = useParams();
  const {
    data: years = [],
    isPending: yearsLoading,
    refetch: refetchYears
  } = useBillingPeriods({ type: "ACADEMIC_YEAR" });
  const { data: terms = [], refetch: refetchTerms } = useBillingPeriods({
    type: "TERM"
  });
  const createTerm = useCreateBillingPeriod();
  const [termLabel, setTermLabel] = useState("");

  const year = useMemo(
    () => years.find((item) => item.id === periodId),
    [years, periodId]
  );

  const yearTerms = useMemo(
    () => terms.filter((term) => term.parentId === periodId),
    [terms, periodId]
  );

  if (yearsLoading) {
    return (
      <CommonLoader
        loadingMessage={t(
          "loading-billing-period",
          "Loading billing period..."
        )}
      />
    );
  }

  if (!year) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>
          {t("billing-period-not-found", "Billing period not found.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="billing-period-detail-page" sx={{ p: 3 }}>
      <Link component={RouterLink} to="..">
        {t("back-to-billing-periods", "Back to billing periods")}
      </Link>

      <Typography variant="h5" component="h1" sx={{ mt: 2, mb: 2 }}>
        {year.label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("academic-year-type", "Academic year")}
      </Typography>

      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("terms-title", "Terms")}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          label={t("term-label", "Term label")}
          value={termLabel}
          onChange={(event) => setTermLabel(event.target.value)}
        />
        <AppButton
          disabled={!termLabel.trim() || createTerm.isPending}
          onClick={async () => {
            await createTerm.mutateAsync({
              type: "TERM",
              label: termLabel.trim(),
              parentId: year.id
            });
            setTermLabel("");
            refetchTerms();
            refetchYears();
          }}
        >
          {t("add-term", "Add term")}
        </AppButton>
      </Stack>

      {yearTerms.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {t("terms-empty", "No terms for this academic year yet.")}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {yearTerms.map((term) => (
            <Box
              component="li"
              key={term.id}
              sx={{
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider"
              }}
            >
              <Typography variant="body2">{term.label}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default BillingPeriodDetail;
